import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import {
  Button,
  TextField,
  Grid,
  Typography,
  Stack,
  Box,
  Paper,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import SnackbarInfo from '../common/snackbar-info.component';
import { updateDataById } from '../../services/videos.service';
import { maxLengthValidation } from '../../utils/validations.util';
import TVODPrice from '../form-elements/tvod-price.component';
import Datepicker from '../form-elements/datepicker.component';
import PublicDomain from '../form-elements/public-domain.component';
import RestrictedContent from '../form-elements/restricted-content.component';

const VideoScheduling = ({
  videoDetail,
  type,
  handleRefreshData,
  formValueChangedCallback,
}) => {
  const {
    control,
    formState: { errors, dirtyFields },
    handleSubmit,
  } = useForm({
    defaultValues: {
      geofencingAllow: videoDetail.geofencingAllow || '',
      geofencingDeny: videoDetail.geofencingDeny || '',
      avod: videoDetail.avod || '',
      avodValidated: videoDetail.avodValidated || 'NC',
      svod: videoDetail.svod || '',
      svodEnabled: videoDetail.svodEnabled === 'YES',
      tvod: videoDetail.tvod || '',
      tvodEnabled: videoDetail.tvodEnabled === 'YES',
      tvodIap: videoDetail.tvodIap || '1',
      publicDomain: videoDetail.publicDomain || 'NO',
      customRoyality: videoDetail.customRoyality || 0,
      dateLicenseExpiration: videoDetail.dateLicenseExpiration || '',
    },
  });

  const [formSubmitted, setFormSubmitted] = useState(false);
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });

  const onFormSubmit = (data) => {
    setFormSubmitted(true);
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });

    const payload = {
      ...data,
      avod: data.avod ? dayjs(data.avod).format('YYYY-MM-DD') : '',
      svod: data.svod ? dayjs(data.svod).format('YYYY-MM-DD') : '',
      tvod: data.tvod ? dayjs(data.tvod).format('YYYY-MM-DD') : '',
      svodEnabled: data.svodEnabled ? 'YES' : 'NO',
      tvodEnabled: data.tvodEnabled ? 'YES' : 'NO',
      dateLicenseExpiration: data.dateLicenseExpiration
        ? dayjs(data.dateLicenseExpiration).format('YYYY-MM-DD')
        : '',
      type,
    };
    updateDataById(videoDetail.id, payload)
      .then(() => {
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'success',
          message: 'Rights Management data updated successfully.',
        });
        formValueChangedCallback(false);
        handleRefreshData();
        setFormSubmitted(false);
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setFormSubmitted(false);
      });
  };

  useEffect(() => {
    if (Object.keys(dirtyFields).length > 0) {
      formValueChangedCallback(true);
    } else {
      formValueChangedCallback(false);
    }
  }, [Object.keys(dirtyFields).length]);

  return (
    <Box component={Paper} padding={2}>
      <form id="scheduling-form" onSubmit={handleSubmit(onFormSubmit)}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="geofencingAllow"
              control={control}
              rules={maxLengthValidation(1024)}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Geofencing Allow"
                  type="text"
                  error={!!errors.geofencingAllow}
                  helperText={errors?.geofencingAllow?.message || null}
                  fullWidth
                  variant="filled"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="geofencingDeny"
              control={control}
              rules={maxLengthValidation(1024)}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Geofencing Deny"
                  type="text"
                  error={!!errors.geofencingDeny}
                  helperText={errors?.geofencingDeny?.message || null}
                  fullWidth
                  variant="filled"
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="avod"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Datepicker
                  id="avod"
                  name="avod"
                  label="AVOD Street Date"
                  defaultValue={value}
                  clearable
                  onChange={onChange}
                  sx={{ width: '100%' }}
                  removePadding
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="avodValidated"
              control={control}
              render={({ field: { onChange, value } }) => (
                <RestrictedContent
                  id="avod_validated"
                  name="avod_validated"
                  label="AVOD License"
                  defaultValue={value}
                  onChange={onChange}
                  sx={{ width: '100%' }}
                  error={errors?.avodValidated?.message || ''}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="svod"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Datepicker
                  id="svod"
                  name="svod"
                  label="SVOD Street Date"
                  defaultValue={value}
                  clearable
                  onChange={onChange}
                  sx={{ width: '100%' }}
                  removePadding
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="svodEnabled"
              control={control}
              render={({ field: { onChange, value } }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={value}
                      onChange={onChange}
                      inputProps={{ 'aria-label': 'controlled' }}
                      color="primary"
                    />
                  }
                  label="SVOD License"
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="tvod"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Datepicker
                  id="tvod"
                  name="tvod"
                  label="TVOD Street Date"
                  defaultValue={value}
                  clearable
                  onChange={onChange}
                  sx={{ width: '100%' }}
                  removePadding
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="tvodEnabled"
              control={control}
              render={({ field: { onChange, value } }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={value}
                      onChange={onChange}
                      inputProps={{ 'aria-label': 'controlled' }}
                      color="primary"
                    />
                  }
                  label="TVOD License"
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="tvodIap"
              control={control}
              render={({ field: { onChange, value } }) => (
                <TVODPrice
                  id="tvod_iap"
                  name="tvod_iap"
                  label="TVOD Price"
                  defaultValue={value}
                  onChange={onChange}
                  sx={{ width: '100%' }}
                  error={errors?.tvodIap?.message || ''}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6} />

          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="publicDomain"
              control={control}
              render={({ field: { onChange, value } }) => (
                <PublicDomain
                  id="public_domain"
                  name="public_domain"
                  label="Public Domain"
                  defaultValue={value}
                  onChange={onChange}
                  sx={{ width: '100%' }}
                  error={errors?.publicDomain?.message || ''}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="customRoyality"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Custom Royality"
                  type="text"
                  error={!!errors.customRoyality}
                  helperText={errors?.customRoyality?.message || null}
                  fullWidth
                  variant="filled"
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="dateLicenseExpiration"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Datepicker
                  id="fl_start_date"
                  name="fl_start_date"
                  label="License Expiration Date"
                  defaultValue={value}
                  clearable
                  onChange={onChange}
                  sx={{ width: '100%' }}
                  removePadding
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6} />
        </Grid>
      </form>

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ py: 2.5 }}
      >
        <Button
          type="submit"
          form="scheduling-form"
          color="primary"
          variant="contained"
          disabled={formSubmitted}
        >
          Save Rights Management
        </Button>
        <Typography variant="body2" color="secondary">
          * indicates a required field
        </Typography>
      </Stack>

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </Box>
  );
};

VideoScheduling.propTypes = {
  videoDetail: PropTypes.object.isRequired,
  type: PropTypes.string.isRequired,
  handleRefreshData: PropTypes.func.isRequired,
  formValueChangedCallback: PropTypes.func.isRequired,
};

export default VideoScheduling;
